/**
 * Map wire / tool progress labels to short user-facing copy.
 * Strips implementation leakage (GQL, expert phases, batch tool names).
 */

import { parseTimestampMs } from "../../../util/parseTimestampMs";

const EXACT: Record<string, string> = {
  // Q&A batch query phases (legacy + current)
  "gql expert phase 1": "Planning search",
  "gql expert phase 1 validation retry": "Refining search",
  "execute phase 1": "Collecting data",
  "execute phase 1 retry": "Retrying data collection",
  "execute phase 2": "Gathering related details",
  "auto paginate": "Collecting more results",
  "input validation": "Checking request",
  "schema load": "Preparing",
  "unexpected error": "Something went wrong",
  // Friendly source labels (pass-through)
  "planning search": "Planning search",
  "refining search": "Refining search",
  "collecting data": "Collecting data",
  "retrying data collection": "Retrying data collection",
  "gathering related details": "Gathering related details",
  "collecting more results": "Collecting more results",
  "checking request": "Checking request",
  preparing: "Preparing",
  "building dashboard": "Building dashboard",
  "running query": "Running query",
  searching: "Searching",
  generating: "Generating",
  formatting: "Formatting",
  // Tool display names
  "get & execute teamform queries (batch)": "Collecting data",
  "get & execute teamform queries": "Collecting data",
  "get teamform query": "Preparing query",
  "execute teamform query": "Running query",
  "build dashboard": "Building dashboard",
  "search people, teams & tags": "Searching",
  "filter people, teams & tags": "Filtering",
  "get person details": "Looking up person",
  "get team details": "Looking up team",
  "find divisions": "Finding divisions",
  "find people by tag": "Finding people",
  "find teams by tag": "Finding teams",
  "get principle status": "Checking principles",
  "get allocations by person": "Checking allocations",
  "get allocations by team": "Checking allocations",
  "get objectives": "Getting objectives",
};

const TOOL_KEYS: Record<string, string> = {
  gql_expert_phase_1: "Planning search",
  gql_expert_phase_1_validation_retry: "Refining search",
  execute_phase_1: "Collecting data",
  execute_phase_1_retry: "Retrying data collection",
  execute_phase_2: "Gathering related details",
  auto_paginate: "Collecting more results",
  get_and_execute_teamform_queries: "Collecting data",
  get_custom_teamform_query: "Preparing query",
  execute_custom_teamform_query: "Running query",
  build_dashboard: "Building dashboard",
  text_search: "Searching",
  parameter_filter: "Filtering",
  get_person: "Looking up person",
  get_group: "Looking up team",
  get_divisions: "Finding divisions",
  get_people_by_tag: "Finding people",
  get_teams_by_tag: "Finding teams",
  get_principle_status: "Checking principles",
  get_allocations_person: "Checking allocations",
  get_allocations_team: "Checking allocations",
  get_objectives: "Getting objectives",
  find_person_by_email: "Finding person",
};

function normalizeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function toSnake(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * @returns Friendly label, or null when input is empty.
 */
export function formatUserFacingProgressLabel(
  raw: string | null | undefined
): string | null {
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return null;
  }

  const key = normalizeKey(trimmed);
  if (EXACT[key]) {
    return EXACT[key];
  }

  const snake = toSnake(trimmed);
  if (TOOL_KEYS[snake]) {
    return TOOL_KEYS[snake];
  }

  if (/gql|graphql/.test(key)) {
    if (/phase\s*2|follow/.test(key)) {
      return "Gathering related details";
    }
    if (/execut|run|collect/.test(key)) {
      return "Collecting data";
    }
    if (/retry|valid/.test(key)) {
      return "Refining search";
    }
    return "Planning search";
  }

  if (/auto\s*paginat/.test(key)) {
    return "Collecting more results";
  }
  if (/build.*dashboard|dashboard.*build/.test(key)) {
    return "Building dashboard";
  }
  if (/get\s*&\s*execute|execute\s*teamform|teamform\s*quer/.test(key)) {
    return "Collecting data";
  }

  const looksTechnical =
    /[_]|gql|graphql|expert|phase\s*\d+|batch|internal|agent|tool/i.test(
      trimmed
    );

  // Already sentence-like and non-technical — keep.
  if (!looksTechnical) {
    return trimmed.replace(/\s+/g, " ");
  }

  // Unknown technical labels require an explicit mapping above.
  return "Working";
}

export function formatElapsedSeconds(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  if (sec < 60) {
    return `${sec}s`;
  }
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export function formatElapsedMs(ms: number): string {
  return formatElapsedSeconds(Math.round(ms / 1000));
}

/** Wall-clock for a finished assistant turn (prefer explicit started/ended). */
export function getMessageDurationMs(
  message:
    | {
        startedAt?: string | null;
        endedAt?: string | null;
        createdAt?: string | null;
      }
    | null
    | undefined
): number | null {
  if (!message) {
    return null;
  }
  const start = parseTimestampMs(message.startedAt ?? message.createdAt);
  const end = parseTimestampMs(message.endedAt);
  if (start == null || end == null) {
    return null;
  }
  const ms = end - start;
  if (Number.isNaN(ms) || ms < 0) {
    return null;
  }
  return ms;
}
